import {
  GQL_ADD_ITEMS_TO_CART,
  GQL_ADD_ITEM_TO_CART,
  GQL_GET_CART,
  GQL_REMOVE_ITEM_FROM_CART,
  GQL_UPDATE_ITEMS_IN_CART,
} from "@/graphql/cart.graphql";
import { ICartState } from "@/interfaces/cart.interface";
import { cartState } from "@/stores/cart";
import { currentUserState } from "@/stores/common";
import { useLazyQuery, useMutation } from "@apollo/react-hooks";
import { message } from "antd";
import { useMemo } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import useTranslation from "./useTranslation";

export const useCart = () => {
  const currentUser = useRecoilValue(currentUserState);

  const [_getCart, { data, loading: loadingGet }] = useLazyQuery<{
    getCart: ICartState;
  }>(GQL_GET_CART, {
    fetchPolicy: "cache-and-network",
    ssr: false,
  });

  const [cart, setCart] = useRecoilState(cartState);

  const getCart = () => {
    if (currentUser.id) {
      _getCart();
    }
  };

  const refetch = () => {
    getCart();
  };

  const [_removeItemFromCart, { loading: loadingRemove }] = useMutation<
    { removeItemFromCart: ICartState },
    { id: string }
  >(GQL_REMOVE_ITEM_FROM_CART);

  useMemo(() => {
    if (data) {
      setCart(data?.getCart);
    }
  }, [data]);

  const handleRemove = async (id: string) => {
    const {
      data: { removeItemFromCart },
    } = await _removeItemFromCart({ variables: { id } });
    setCart(removeItemFromCart);
  };

  const loading = useMemo(() => loadingGet || loadingRemove, [
    loadingGet,
    loadingRemove,
  ]);

  return {
    getCart,
    loading,
    loadingGet,
    loadingRemove,
    handleRemove,
    refetch,
  };
};

export const useAddToCart = () => {
  const [_addItemToCart, { loading }] = useMutation<
    { addItemToCart: ICartState },
    { id: string }
  >(GQL_ADD_ITEM_TO_CART);

  const { t } = useTranslation("bundle");

  const [cart, setCart] = useRecoilState(cartState);

  const { refetch } = useCart();

  const handleAddToCart = async (id: string) => {
    const {
      data: { addItemToCart },
    } = await _addItemToCart({ variables: { id } });
    setCart(addItemToCart);
    refetch();
    message.success(t("cart.add.message"));
  };

  return {
    loading,
    handleAddToCart,
  };
};

export const useAddItemsToCart = () => {
  const [_addItemsToCart, { loading }] = useMutation<
    { addItemsToCart: ICartState },
    { ids: string[] }
  >(GQL_ADD_ITEMS_TO_CART);

  const { t } = useTranslation("bundle");

  const [cart, setCart] = useRecoilState(cartState);

  const handleAddToCart = async (ids: string[]) => {
    const {
      data: { addItemsToCart },
    } = await _addItemsToCart({ variables: { ids } });
    setCart(addItemsToCart);
    message.success(t("cart.add.message"));
  };

  return {
    loading,
    handleAddToCart,
  };
};

export const useUpdateItemsInCart = () => {
  const [_updateItemsInCart, { loading }] = useMutation<
    { updateItemsInCart: ICartState },
    { ids: string[] }
  >(GQL_UPDATE_ITEMS_IN_CART);
  const [cart, setCart] = useRecoilState(cartState);
  const updateItemsInCart = async (ids: string[]) => {
    const { data } = await _updateItemsInCart({
      variables: { ids: ids.filter((e) => e) },
    });
    setCart(data?.updateItemsInCart);
  };
  return {
    loading,
    updateItemsInCart,
  };
};
